import React, { useState } from 'react';
import { Dialog } from './common/Dialog';
import * as styles from '../styles/components/tab-creation-modal.css.ts';

export interface TabOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  description?: string;
  requiresSelection?: boolean; // 選択が必要なタブタイプ
}

export interface SelectableItem {
  id: string;
  name: string;
  description?: string;
}

interface TabCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: TabOption[];
  onSelectOption: (optionId: string, selectedItemId?: string) => void;
  getSelectableItems?: (optionId: string) => SelectableItem[]; // 選択可能なアイテムを取得
}

export const TabCreationModal: React.FC<TabCreationModalProps> = ({
  isOpen,
  onClose,
  options,
  onSelectOption,
  getSelectableItems,
}) => {
  const [selectedTabType, setSelectedTabType] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedTabType(null);
    onClose();
  };

  const handleOptionClick = (option: TabOption) => {
    if (option.requiresSelection && getSelectableItems) {
      // 選択が必要な場合は選択画面へ
      setSelectedTabType(option.id);
    } else {
      // 選択不要な場合は直接作成
      onSelectOption(option.id);
      handleClose();
    }
  };

  const handleItemSelect = (itemId: string) => {
    if (selectedTabType) {
      onSelectOption(selectedTabType, itemId);
      handleClose();
    }
  };

  const handleBack = () => {
    setSelectedTabType(null);
  };

  // 選択画面を表示
  if (selectedTabType && getSelectableItems) {
    const items = getSelectableItems(selectedTabType);
    const selectedOption = options.find(opt => opt.id === selectedTabType);

    return (
      <Dialog
        isOpen={true}
        onClose={handleClose}
        title={`Select ${selectedOption?.label || 'Item'}`}
        width="md"
      >
        <div>
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Back"
            style={{ marginBottom: '16px' }}
          >
            ← Back
          </button>
          <div className={styles.itemList}>
            {items.length > 0 ? (
              items.map((item) => (
                <button
                  key={item.id}
                  className={styles.itemButton}
                  onClick={() => handleItemSelect(item.id)}
                >
                  <div className={styles.itemName}>{item.name}</div>
                  {item.description && (
                    <div className={styles.itemDescription}>
                      {item.description}
                    </div>
                  )}
                </button>
              ))
            ) : (
              <div className={styles.emptyState}>
                No {selectedOption?.label?.toLowerCase() || 'items'} available
              </div>
            )}
          </div>
        </div>
      </Dialog>
    );
  }

  // タブタイプ選択画面を表示
  return (
    <Dialog
      isOpen={true}
      onClose={handleClose}
      title="Create New Tab"
      width="lg"
    >
      <div className={styles.optionGrid}>
        {options.map((option) => (
          <button
            key={option.id}
            className={option.disabled ? `${styles.optionButton} ${styles.disabled}` : styles.optionButton}
            onClick={() => handleOptionClick(option)}
            disabled={option.disabled}
            title={option.description}
          >
            {option.icon && (
              <div className={styles.optionIcon}>{option.icon}</div>
            )}
            <div className={styles.optionLabel}>{option.label}</div>
            {option.description && (
              <div className={styles.optionDescription}>
                {option.description}
              </div>
            )}
          </button>
        ))}
      </div>
    </Dialog>
  );
};